import React, { useMemo, memo } from 'react';
import { isEmpty } from 'lodash-es';
import type { EChartsOption } from 'echarts';
import type { IAgentEchartProps, TAgentEchartValuePayload } from './types';
import { DEFAULT_COLOR_LIST } from './types';
import './index.scss';
import Chart from '@/pages/dashboard/components/Chart';

/**
 * 默认 grid 配置：
 * - containLabel: true 让 grid 区域计算时包含坐标轴标签，echarts 会自动为标签预留空间
 * - left/right/top/bottom 仅作为兜底间距（贴近图例/标题），containLabel 会在此基础上再扩出标签所需空间
 * 用户后端返回的 option.grid 字段会覆盖此默认值（用户优先）
 */
const GRID_TOP_FOR_TITLE_LEGEND = 48;
const GRID_BOTTOM_PADDING = 8;
const DEFAULT_GRID = {
  left: 0,
  right: 0,
  top: GRID_TOP_FOR_TITLE_LEGEND,
  bottom: GRID_BOTTOM_PADDING,
  containLabel: true,
};

/**
 * 默认 axisLabel 配置：
 * - hideOverlap: true 标签密集（如长日期）时自动隐藏重叠项，避免被截断或叠压
 * 用户传入的 axisLabel 字段优先级更高
 */
const DEFAULT_AXIS_LABEL = {
  hideOverlap: true,
};

type TAxisObject = { axisLabel?: Record<string, unknown>; [key: string]: unknown; };
type TAxisInput = TAxisObject | TAxisObject[] | undefined | null;

/**
 * 为单个轴对象注入默认 axisLabel（用户传入的 axisLabel 字段优先）
 */
const mergeAxisLabel = (axis: TAxisObject | null | undefined): TAxisObject | null | undefined => {
  if (!axis || typeof axis !== 'object') return axis;
  return {
    ...axis,
    axisLabel: { ...DEFAULT_AXIS_LABEL, ...(axis.axisLabel || {}) },
  };
};

/**
 * 兼容轴配置为对象 / 数组 / 缺省 三种形态，统一注入默认 axisLabel
 */
const mergeAxis = (axis: TAxisInput): TAxisInput => {
  if (axis == null) return axis;
  if (Array.isArray(axis)) return axis.map((item) => mergeAxisLabel(item) as TAxisObject);
  return mergeAxisLabel(axis);
};

/**
 * AgentEchart组件
 * 基于echarts-for-react封装的图表组件，支持自定义配置和颜色。
 *
 * 数据来源优先级：显式 props（options/height/width/color）> value（流式 item.value 透传）
 */
const AgentEchart = memo(({
  value,
  options: optionsProp,
  color: colorProp,
  height: heightProp,
  width: widthProp,
  className,
  style,
}: IAgentEchartProps) => {
  // 数据来源优先级：显式 props > value 内对应字段 > 默认值
  const optionsSource: TAgentEchartValuePayload | EChartsOption | undefined =
    optionsProp ?? value;
  const optionsAsPayload = optionsSource as TAgentEchartValuePayload | undefined;
  const color = colorProp ?? value?.color ?? optionsAsPayload?.color ?? DEFAULT_COLOR_LIST;
  const height = heightProp ?? value?.height ?? optionsAsPayload?.height ?? 300;
  const width = widthProp ?? value?.width ?? optionsAsPayload?.width ?? '100%';

  // 注入默认 grid 防止轴标签被遮挡，用户传入的 grid 字段优先；
  const mergedOptions = useMemo(() => {
    if (!optionsSource) return optionsSource;
    // 剥离 height/width/color，容器尺寸/配色由外层控制，与 echarts option 解耦
    const { height: _h, width: _w, color: _c, ...echartsOption } =
      optionsSource as TAgentEchartValuePayload;
    const userGrid = (echartsOption as { grid?: Record<string, unknown>; }).grid;
    const mergedGrid = !isEmpty(userGrid)
      ? { ...DEFAULT_GRID, ...userGrid }
      : DEFAULT_GRID;
    const {xAxis} = (echartsOption as { xAxis?: TAxisInput; });
    const {yAxis} = (echartsOption as { yAxis?: TAxisInput; });
    return {
      ...echartsOption,
      color,
      grid: mergedGrid,
      // 为 xAxis / yAxis 默认注入 hideOverlap，避免长日期等标签被遮盖/截断
      ...(xAxis !== undefined && { xAxis: mergeAxis(xAxis) }),
      ...(yAxis !== undefined && { yAxis: mergeAxis(yAxis) }),
    };
  }, [optionsSource, color]);

  // 无数据时不渲染，避免传入空 options 导致 echarts 报错
  if (!optionsSource) {
    return null;
  }
  return (
    <div
      className={`agent-echart-container ${className || ''}`}
      style={{ width, height, ...style }}
    >
      <Chart
        option={mergedOptions}
        style={{ height: '100%', width: '100%' }}
        notMerge={true}
        wrapperDom={
          document.getElementsByClassName('dbh-agent-copilot')?.[0] ||
          document.getElementsByClassName('.dbh-agent-iwiki')?.[0]
        }
      />
    </div>
  );
}, (prevProps, nextProps) => (
  // 同时影响渲染结果的 props 都参与比较，否则显式 props 模式下变更不会重渲染
  prevProps.value === nextProps.value &&
  prevProps.messageId === nextProps.messageId
));

AgentEchart.displayName = 'AgentEchart';

export default AgentEchart;
